import trace from '~/lib/trace';
import React, {useState, useEffect} from 'react';
import {View, ActivityIndicator, ScrollView} from 'react-native';
import {Divider} from 'react-native-paper';

import sy from '~/styles';
import {
  Pressable,
  Text,
  Link,
  Dialog,
  FormTextInput,
  Select,
  RadioButton,
  ActionButton,
} from '~/components/controls';
import Format from '~/lib/format';
import Actionsheet, {Title, Actions} from '~/components/controls/Actionsheet';
import withDimensions from '~/components/hoc/with-dimensions';

import {
  car as carApi,
  dealer as dealerApi,
  rob as robApi,
  service_request as serviceRequestApi,
} from '~/api/private';

import AddIcon from '~/images/md-icons/add/materialicons/24px.svg';
import DeleteIcon from '~/images/md-icons/delete/materialicons/24px.svg';
import PropTypes from 'prop-types';

const HEADER_HEIGHT_IN_PX = 70;
const FOOTER_HEIGHT_IN_PX = 50;
const DIVIDER_HEIGHT_IN_PX = 1;

const RentalClassText = ({
  style,
  rental_class,
  rental_classes,
  contract_rental_class = null,
  selected_rental_class = null,
}) => {
  let labelSuffix = null;
  if (
    rental_class === contract_rental_class &&
    rental_class === selected_rental_class
  ) {
    labelSuffix = <>&nbsp;(contract&nbsp;&amp;&nbsp;inzet)</>;
  } else if (rental_class === contract_rental_class) {
    labelSuffix = <>&nbsp;(contract)</>;
  } else if (rental_class === selected_rental_class) {
    labelSuffix = <>&nbsp;(inzet)</>;
  }

  return (
    <Text style={style}>
      {rental_class}-
      {rental_classes?.find((item) => item.code === rental_class)?.description}
      {labelSuffix}
    </Text>
  );
};
RentalClassText.propTypes = {
  style: PropTypes.any,
  rental_class: PropTypes.any,
  rental_classes: PropTypes.any,
  contract_rental_class: PropTypes.any,
  selected_rental_class: PropTypes.any,
};

const RentalCarActionsheet = ({
  dimensions,
  visible,
  request_id,
  is_fleet_car,
  dealer_id,
  license,
  fuel,
  rental_class,
  contract_rental_class,
  onChange,
  onClose,
}) => {
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(license);
  const [rentalClasses, setRentalClasses] = useState(null);
  const [addCar, setAddCar] = useState(null);
  const [rentalCars, setRentalCars] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      const {result: rentalClassesResult} = await robApi.rental_classes();
      setRentalClasses(rentalClassesResult);

      const {result: rental_cars} = await dealerApi.rental_cars({
        dealer_id,
      });
      setRentalCars(rental_cars);

      setLoading(false);
    };

    fetch().catch(trace);
  }, [dealer_id]);

  useEffect(() => {
    setSelected(license);
  }, [license, visible]);

  const onAddCar = async () => {
    if (
      !addCar?.license ||
      !addCar?.name ||
      !addCar?.gearbox ||
      !addCar?.rental_class
    ) {
      return;
    }

    const {
      license,
      name,
      rental_class,
      dealer_id: add_car_dealer_id,
      ...attributes
    } = addCar;

    const result = await dealerApi.addEditRentalCar(
      add_car_dealer_id ?? dealer_id,
      {
        license,
        name,
        attributes: {
          ...attributes,
          rental_class,
        },
      },
    );

    if (result.success) {
      const {result: rental_cars} = await dealerApi.rental_cars({dealer_id});
      setRentalCars(rental_cars);

      setAddCar(null);
      setSelected(license);
    }
  };

  const onDeleteCar = async () => {
    const result = await dealerApi.deleteRentalCar(addCar.id);

    if (result.success) {
      const {result: rental_cars} = await dealerApi.rental_cars({dealer_id});
      setRentalCars(rental_cars);
      setAddCar(null);
    }
  };

  const onOK = async () => {
    const car = rentalCars.find((item) => item.license === selected);
    if (car) {
      const {result} =
        (await serviceRequestApi.rental_vehicle(request_id, selected)) ?? {};
      const {rental_class} = result ?? {};

      onChange({
        license: car.license,
        name: car.name,
        rental_class: rental_class?.code ?? car.attributes.rental_class,
        fuel: rental_class?.fuel ?? car.attributes.fuel,
        gearbox: rental_class?.gearbox ?? car.attributes.gearbox,
        is_estate: rental_class?.is_estate ?? car.attributes.is_estate,
      });
    } else {
      onChange(null);
    }

    onClose();
  };

  return (
    <>
      <Actionsheet visible={visible} onDismiss={onClose}>
        <Title>Auto inzet</Title>
        <Divider style={sy['bg-gray88']} />
        {loading && (
          <View style={[sy['flex-1'], sy['p-4']]}>
            <ActivityIndicator size="large" color="#231fda" />
          </View>
        )}
        {!loading && (
          <>
            <ScrollView
              contentContainerStyle={{
                maxHeight:
                  dimensions.window.height -
                  HEADER_HEIGHT_IN_PX -
                  DIVIDER_HEIGHT_IN_PX -
                  FOOTER_HEIGHT_IN_PX -
                  DIVIDER_HEIGHT_IN_PX,
              }}>
              {rentalCars.length > 0 && (
                <View style={[sy['flex-row']]}>
                  <Pressable
                    onPress={() => {
                      setSelected(null);
                    }}>
                    <View style={sy['p-4']}>
                      <RadioButton checked={!selected} />
                    </View>
                  </Pressable>
                  <View
                    style={[
                      sy['py-4'],
                      sy['border-b'],
                      sy['border-lightgray'],
                      sy['flex-1'],
                    ]}>
                    <Text>Geen keuze</Text>
                  </View>
                </View>
              )}
              {rentalCars?.map((car) => (
                <View key={car.license} style={[sy['flex-row']]}>
                  <Pressable
                    onPress={() => {
                      setSelected(car.license);
                    }}>
                    <View style={sy['p-4']}>
                      <RadioButton checked={car.license === selected} />
                    </View>
                  </Pressable>
                  <View
                    style={[
                      sy['py-4'],
                      sy['border-b'],
                      sy['border-lightgray'],
                      sy['flex-1'],
                    ]}>
                    <Text>
                      {Format.license(car.license)}&nbsp;&middot;&nbsp;
                      {car.name}
                    </Text>

                    <Link
                      style={{display: 'inline', flex: 0}}
                      onPress={() =>
                        setAddCar({
                          ...car,
                          ...car.attributes,
                        })
                      }>
                      <RentalClassText
                        style={[sy.small, sy['text-lightgray']]}
                        rental_class={car.attributes?.rental_class}
                        rental_classes={rentalClasses}
                        contract_rental_class={contract_rental_class}
                        selected_rental_class={rental_class}
                      />
                    </Link>
                  </View>
                </View>
              ))}
              <Pressable
                style={[sy['flex-row'], sy['p-4'], sy['items-center']]}
                onPress={() => {
                  setAddCar({
                    rental_class: rentalClasses[0].code,
                    fuel: 'gasoline',
                    gearbox: 'manual',
                    is_estate: false,
                  });
                }}>
                <AddIcon fill="#2907E3" />
                <Text style={[sy['pl-4']]}>Auto toevoegen</Text>
              </Pressable>
            </ScrollView>
            <Divider style={sy['bg-gray88']} />
            <Actions>
              <ActionButton onPress={onClose}>Annuleren</ActionButton>
              <ActionButton onPress={onOK}>OK</ActionButton>
            </Actions>
          </>
        )}
      </Actionsheet>
      <Dialog
        visible={!!addCar}
        title={
          addCar?.id ? (
            <View
              style={[
                sy['flex-row'],
                sy['justify-between'],
                sy['items-center'],
                {
                  display: 'flex',
                },
              ]}>
              <Text style={[sy.large]}>Huurauto bewerken</Text>
            </View>
          ) : (
            <Text style={[sy.large]}>Huurauto toevoegen</Text>
          )
        }
        titleIcon={
          addCar?.id ? (
            <Pressable onPress={() => onDeleteCar()}>
              <DeleteIcon fill="#4A4A49" />
            </Pressable>
          ) : null
        }
        onDismiss={() => setAddCar(null)}
        buttons={[
          {
            text: 'Annuleren',
            onPress: () => setAddCar(null),
          },
          {
            text: 'OK',
            disabled:
              !addCar?.license || !addCar?.name || !addCar?.rental_class,
            onPress: onAddCar,
          },
        ]}>
        <View style={[sy['py-4']]}>
          <FormTextInput
            value={addCar?.license}
            onChangeText={(text) => {
              const license = text?.replace(/[^0-9a-z]/gi, '').toUpperCase();
              setAddCar({
                ...addCar,
                license,
                name: null,
              });
            }}
            onEndEditing={async (license) => {
              if (license?.length > 5) {
                const car = await carApi.fetch(license);

                let rental_vehicle = null;
                if (!is_fleet_car) {
                  const {success, result} =
                    await serviceRequestApi.rental_vehicle(request_id, license);

                  if (success) {
                    rental_vehicle = result;
                  }
                }

                setAddCar((prevState) => ({
                  ...prevState,
                  license,
                  name: car.name,
                  rental_class: rental_vehicle?.rental_class?.code ?? null,
                  ...rental_vehicle?.rental_class,
                }));
              }
            }}
            label="Kenteken"
            required={true}
            errorMessage={'Kenteken is verplicht'}
            inputProps={{
              maxLength: 8,
              autoCapitalize: 'characters',
            }}
          />
          {addCar?.name && (
            <Text style={[sy['pl-2'], sy['text-lightgray']]}>
              {addCar.name}
            </Text>
          )}
        </View>
        <View style={[sy['py-4'], sy['pt-0']]}>
          <Select
            label="Klasse"
            value={addCar?.rental_class}
            options={rentalClasses?.map((item) => ({
              text: `${item.code} - ${item.description}`,
              value: item.code,
            }))}
            onChange={(option) =>
              setAddCar((prevState) => ({
                ...prevState,
                rental_class: option.value,
              }))
            }
          />
        </View>
        <View style={[sy['py-4'], sy['pt-0']]}>
          <Select
            label="Brandstof"
            value={addCar?.fuel ?? fuel}
            options={[
              {text: 'Benzine', value: 'gasoline'},
              {text: 'Diesel', value: 'diesel'},
              {text: 'Elektrisch', value: 'electric'},
              {text: 'Hybride', value: 'hybrid'},
              {text: 'LNG', value: 'lng'},
              {text: 'LPG', value: 'lpg'},
              {text: 'Overig', value: 'other'},
            ]}
            onChange={(option) =>
              setAddCar((prevState) => ({
                ...prevState,
                fuel: option.value,
              }))
            }
          />
        </View>
        <View style={[sy['py-4'], sy['pt-0']]}>
          <Select
            label="Transmissie"
            value={addCar?.gearbox ?? 'manual'}
            options={[
              {text: 'Handgeschakeld', value: 'manual'},
              {text: 'Automaat', value: 'automatic'},
            ]}
            onChange={(option) =>
              setAddCar((prevState) => ({
                ...prevState,
                gearbox: option.value,
              }))
            }
          />
        </View>
        <View style={[sy['py-4'], sy['pt-0']]}>
          <Select
            label="Carrosserievorm"
            value={`${addCar?.is_estate ?? false}`}
            options={[
              {text: 'Overig', value: 'false'},
              {text: 'Stationwagon', value: 'true'},
            ]}
            onChange={(option) =>
              setAddCar((prevState) => ({
                ...prevState,
                is_estate: option.value === 'true',
              }))
            }
          />
        </View>
      </Dialog>
    </>
  );
};
RentalCarActionsheet.propTypes = {
  dimensions: PropTypes.any,
  visible: PropTypes.any,
  request_id: PropTypes.any,
  is_fleet_car: PropTypes.any,
  dealer_id: PropTypes.any,
  license: PropTypes.any,
  fuel: PropTypes.any,
  rental_class: PropTypes.any,
  contract_rental_class: PropTypes.any,
  onChange: PropTypes.any,
  onClose: PropTypes.any,
};

export default withDimensions(RentalCarActionsheet);
